import { safeParseFloat } from "@corpuscomplete/core/utils";
import { z } from "zod/v4";

export namespace NumericTarget {
  export namespace Exact {
    export const schema = (opts?: { value: z.ZodType<number> }) =>
      z.object({
        type: z.literal("exact"),
        value: opts?.value ?? z.number(),
      });
    export type Info = z.infer<ReturnType<typeof schema>>;

    export const asString = (target: Info): string => {
      return target.value.toString();
    };
    export const fromString = (input: string): Info | null => {
      const value = safeParseFloat(input);
      if (value === null) {
        return null;
      }
      return {
        type: "exact",
        value,
      };
    };
  }

  export namespace Between {
    export const schema = (opts?: { min: z.ZodType<number>; max: z.ZodType<number> }) =>
      z.object({
        type: z.literal("between"),
        min: opts?.min ?? z.number(),
        max: opts?.max ?? z.number(),
      });
    export type Info = z.infer<ReturnType<typeof schema>>;
  }

  export namespace AtLeast {
    export const schema = (opts?: { value: z.ZodType<number> }) =>
      z.object({
        type: z.literal("atLeast"),
        value: opts?.value ?? z.number(),
      });
    export type Info = z.infer<ReturnType<typeof schema>>;
  }

  export namespace AtMost {
    export const schema = (opts?: { value: z.ZodType<number> }) =>
      z.object({
        type: z.literal("atMost"),
        value: opts?.value ?? z.number(),
      });
    export type Info = z.infer<ReturnType<typeof schema>>;
  }

  export namespace Max {
    export const schema = () =>
      z.object({
        type: z.literal("max"),
      });
    export type Info = z.infer<ReturnType<typeof schema>>;
  }

  type Any = Exact.Info | Between.Info | AtLeast.Info | AtMost.Info | Max.Info;
  export type Type = Any["type"];

  export type Info<T extends Type = Type> = Extract<Any, { type: T }>;

  export function schema(options?: {
    value?: z.ZodType<number>;
    variants?: readonly Type[];
  }): z.ZodType<Info> {
    const value = options?.value ?? z.number();
    const variants =
      options?.variants ?? (["exact", "between", "atLeast", "atMost", "max"] as const);
    const schemas = variants.map((variant) => {
      switch (variant) {
        case "exact":
          return Exact.schema({ value });
        case "between":
          return Between.schema({ min: value, max: value });
        case "atLeast":
          return AtLeast.schema({ value });
        case "atMost":
          return AtMost.schema({ value });
        case "max":
          return Max.schema();
      }
    });

    if (schemas.length === 0) {
      return z.never() as z.ZodType<Info>;
    }

    if (schemas.length === 1) {
      return schemas[0] as z.ZodType<Info>;
    }

    return z.union(schemas as unknown as [z.ZodType<Info>, z.ZodType<Info>, ...z.ZodType<Info>[]]);
  }

  export type CheckOptions = {
    absoluteError?: number;
    relativeError?: number;
  };

  export type FormatOptions = {
    unitName?: string;
    fractionDigits?: number;
    style?: "compact" | "readable";
  };

  export function format(target: Info, options?: FormatOptions): string {
    const fractionDigits = options?.fractionDigits ?? 0;
    const style = options?.style ?? "compact";
    const unitSeparator = style === "readable" ? " " : "";
    const withUnit = (value: string) =>
      options?.unitName ? `${value}${unitSeparator}${options.unitName}` : value;
    const formatNumber = (value: number) => value.toFixed(fractionDigits);

    switch (target.type) {
      case "exact":
        return withUnit(formatNumber(target.value));
      case "between":
        return withUnit(`${formatNumber(target.min)}-${formatNumber(target.max)}`);
      case "atLeast":
        return style === "readable"
          ? `at least ${withUnit(formatNumber(target.value))}`
          : `>=${withUnit(formatNumber(target.value))}`;
      case "atMost":
        return style === "readable"
          ? `at most ${withUnit(formatNumber(target.value))}`
          : `<=${withUnit(formatNumber(target.value))}`;
      case "max":
        return options?.unitName ? `maximize ${options.unitName}` : "maximize";
    }
  }

  export function check(value: number, target: Info, options?: CheckOptions): boolean {
    const absoluteError = Math.max(options?.absoluteError ?? 0, 0);
    const relativeError = Math.max(options?.relativeError ?? 0, 0);
    const toleranceFor = (reference: number) =>
      Math.max(absoluteError, Math.abs(reference) * relativeError);

    switch (target.type) {
      case "exact": {
        const tolerance = toleranceFor(target.value);
        return Math.abs(value - target.value) <= tolerance;
      }
      case "between":
        return (
          value >= target.min - toleranceFor(target.min) &&
          value <= target.max + toleranceFor(target.max)
        );
      case "atLeast":
        return value >= target.value - toleranceFor(target.value);
      case "atMost":
        return value <= target.value + toleranceFor(target.value);
      case "max":
        return true;
    }
  }
}
